import React from 'react'
import { Label } from '../../components/label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

// Base Label export - for other composed components
export { Label as UILabelBase } from '../../components/label'

export interface UILabelProps extends React.ComponentProps<typeof Label> {
  /**
   * 필수 여부
   */
  required?: boolean
  /**
   * 라벨 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 라벨 변형
   */
  variant?: 'default' | 'muted' | 'destructive'
  /**
   * 설명 텍스트
   */
  description?: string
  /**
   * 인라인 스타일 여부
   */
  inline?: boolean
}

const sizeVariants = {
  sm: "text-xs",
  default: "text-sm",
  lg: "text-base"
}

const variantStyles = {
  default: "",
  muted: "text-muted-foreground",
  destructive: "text-destructive"
}

/**
 * 라벨 컴포넌트
 * 필수 표시, 다양한 크기와 변형, 설명 텍스트를 지원합니다.
 */
export const UILabel = React.forwardRef<
  React.ComponentRef<typeof Label>,
  UILabelProps
>(({
  className,
  required = false,
  size = "default",
  variant = "default",
  description,
  inline = false,
  children,
  ...props
}, ref) => {
  const labelElement = (
    <Label
      ref={ref}
      className={cn(
        sizeVariants[size],
        variantStyles[variant],
        required && "after:content-['*'] after:ml-0.5 after:text-destructive",
        inline && "flex-shrink-0",
        className
      )}
      {...props}
    >
      {children}
    </Label>
  )

  if (!description) {
    return labelElement
  }

  return (
    <UIBox className={cn(
      "space-y-1",
      inline && "flex items-start gap-3 space-y-0"
    )}>
      {labelElement}
      <UITypography
        variant="p"
        size="xs"
        color="muted"
        className={cn(inline && "flex-1")}
      >
        {description}
      </UITypography>
    </UIBox>
  )
})

// 특정 용도별 라벨 컴포넌트들
export const UIFieldLabel = React.forwardRef<
  React.ComponentRef<typeof UILabel>,
  Omit<UILabelProps, 'size'> & {
    error?: string
  }
>(({ error, variant, ...props }, ref) => (
  <UILabel
    ref={ref}
    size="default"
    variant={error ? 'destructive' : variant}
    {...props}
  />
))

export const UIFormSectionLabel = React.forwardRef<
  React.ComponentRef<typeof UILabel>,
  Omit<UILabelProps, 'size' | 'variant'>
>(({ className, ...props }, ref) => (
  <UILabel
    ref={ref}
    size="lg"
    variant="default"
    className={cn("font-semibold", className)}
    {...props}
  />
))

UILabel.displayName = "UILabel"
UIFieldLabel.displayName = "UIFieldLabel"
UIFormSectionLabel.displayName = "UIFormSectionLabel"